import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { Paths } from "@/core/constants/paths";
import type { ExtendedRouteObject, RouteRule, RouteScope } from "@/routes/types";
import { routes } from "@/routes";
import { isAuthorizedForRules } from "./authorization";
import {
  getAuthToken,
  isTokenExpired,
  hasAnyScope,
  decodeJwt,
  SCOPE_HUB_WEB,
} from "./auth.service";

function normalizePath(path?: string | null): string {
  if (!path) return "/";
  const clean = (path.split("?")[0] ?? "/").replace(/\/+$/, "");
  return clean || "/";
}

function buildChildFullPath(parent?: string, child?: string): string {
  if (!child) return parent ?? "/";
  if (child.startsWith("/")) return child;
  if (!parent) return `/${child}`;
  return `${parent.replace(/\/$/, "")}/${child}`;
}

function pathMatchesRoute(routePath: string | undefined, current: string): boolean {
  if (!routePath) return false;
  const rSegs = normalizePath(routePath).split("/").filter(Boolean);
  const cSegs = normalizePath(current).split("/").filter(Boolean);
  if (rSegs.length !== cSegs.length) return false;
  return rSegs.every((seg, i) => seg.startsWith(":") || seg === cSegs[i]);
}

interface FlatRoute {
  path: string;
  rules?: RouteRule[];
  scope?: RouteScope[];
}

function flattenRoutes(
  routeList: ExtendedRouteObject[],
  parentPath?: string,
  parentRules?: RouteRule[],
  parentScope?: RouteScope[],
): FlatRoute[] {
  return routeList.flatMap((route) => {
    const fullPath = route.path ? buildChildFullPath(parentPath, route.path) : parentPath;
    const rules = route.rules ?? parentRules;
    const scope = route.scope ?? parentScope;

    const current: FlatRoute[] = fullPath ? [{ path: normalizePath(fullPath), rules, scope }] : [];
    const children = route.children
      ? flattenRoutes(route.children, fullPath, rules, scope)
      : ([] as FlatRoute[]);

    return [...current, ...children];
  });
}

let flatRoutesCache: FlatRoute[] | null = null;

// Sob demanda: `@/routes` importa telas que reimportam este módulo; achatar na
// avaliação do módulo quebraria o ciclo de import.
function getFlatRoutes(): FlatRoute[] {
  flatRoutesCache ??= flattenRoutes(routes);
  return flatRoutesCache;
}

function findRoute(currentPath: string): FlatRoute | undefined {
  return getFlatRoutes().find((route) => pathMatchesRoute(route.path, currentPath));
}

function isPathAccessible(currentPath: string, role: RouteRule): boolean {
  return getFlatRoutes().some(
    (route) => pathMatchesRoute(route.path, currentPath) && route.rules?.includes(role),
  );
}

function getRequiredScopes(currentPath: string): RouteScope[] | null {
  const route = findRoute(currentPath);
  if (!route) return [SCOPE_HUB_WEB as RouteScope];
  if (route.rules?.includes("public")) return null;
  return route.scope ?? [SCOPE_HUB_WEB as RouteScope];
}

function redirectUnauthenticated() {
  return <Navigate to={Paths.LOGIN} replace />;
}

function redirectForbidden() {
  return <Navigate to={Paths.FORBIDDEN} replace />;
}

interface AuthGuardProps {
  children: ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const location = useLocation();

  const rawPath = location.pathname || "/";
  const currentPath = normalizePath(rawPath);

  // --- Rota pública: libera direto ---
  if (isPathAccessible(currentPath, "public")) {
    return <>{children}</>;
  }

  // --- Verificar autenticação ---
  const token = getAuthToken();
  if (!token || isTokenExpired(token)) {
    return redirectUnauthenticated();
  }

  // --- Verificar scopes ---
  const requiredScopes = getRequiredScopes(currentPath);
  if (requiredScopes && requiredScopes.length > 0) {
    const payload = decodeJwt(token);

    // Tokens internos do BFF ainda não trazem `scope`; libera enquanto o RBAC não estiver pronto.
    if (payload?.scope) {
      const check = requiredScopes.includes("workspace" as RouteScope)
        ? [...requiredScopes, SCOPE_HUB_WEB as RouteScope]
        : requiredScopes;

      if (!hasAnyScope(token, check)) {
        return redirectUnauthenticated();
      }
    }
  }

  // --- Verificar regras da rota ---
  if (!isAuthorizedForRules(findRoute(currentPath)?.rules)) {
    return redirectForbidden();
  }

  return <>{children}</>;
}
